import { useInfiniteQuery } from "@tanstack/vue-query";
import type { ILegalText } from "~/models/ILegalText";

export function usePaginatedParagraphs(
  legalText: MaybeRef<ILegalText>,
  paragraphInitialTocOrder: MaybeRef<number>
) {
  const paginationSize = 10;
  const queryClient = useQueryClient();
  const resolvedQueries = queries();
  const legalTextId = computed(() => toValue(legalText).id);
  const pageOfActiveParagraph = computed(() =>
    Math.floor(toValue(paragraphInitialTocOrder) / paginationSize + 1)
  );
  const {
    status,
    error,
    data,
    refetch: _refetch,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    fetchNextPage,
  } = useInfiniteQuery(
    {
      ...resolvedQueries.legalTexts.byId(legalTextId)._ctx.allParagraphs,
      // TODO: Somehow the types are fucked up here
      initialPageParam: pageOfActiveParagraph as never,
      getNextPageParam: (lastPage) =>
        (lastPage.links.next
          ? lastPage.meta.current_page + 1
          : undefined) as never,
      getPreviousPageParam: (firstPage) =>
        (firstPage.links.prev
          ? firstPage.meta.current_page - 1
          : undefined) as never,
    },
    queryClient
  );

  const paragraphs = computed(() =>
    data.value ? data.value.pages.flatMap((page) => page.data) : []
  );

  // cache paragraphs
  watch(paragraphs, (ps) => {
    ps.forEach((paragraph) => {
      const queryKeys = [
        resolvedQueries.paragraphs.byId(paragraph.id).queryKey,
        resolvedQueries.legalTexts
          .bySlug(toValue(legalText).slug)
          ._ctx.paragraphBySlug(paragraph.slug).queryKey,
      ] as const;

      queryKeys.forEach((key) => {
        queryClient.setQueryData(key, paragraph);
      });
    });
  });

  const resetInfiniteQueryPagination = (): void => {
    queryClient.setQueryData(
      resolvedQueries.legalTexts.byId(legalTextId)._ctx.allParagraphs.queryKey,
      (oldData) => {
        if (!oldData) return undefined;

        return {
          ...oldData,
          // pages: oldData.pages.slice(0, 1),
          // pageParams: oldData.pageParams.slice(0, 1),
          pages: [],
          pageParams: [],
        };
      }
    );
  };

  const refetch = () => {
    resetInfiniteQueryPagination();
    return _refetch();
  };

  return {
    paginationSize,
    status,
    error,
    refetch,
    data: paragraphs,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    fetchNextPage,
  };
}
